import { z } from "zod";

import { eventSchema } from "../shared";

// Resume Optimization Process Events
export const resumeOptimizationStartedEvent = eventSchema.extend({
  event: z.string().default("resume_optimization_started"),
  properties: z.object({
    resumeId: z.string(),
    hasJobDescription: z.boolean(),
    hasAnswers: z.boolean(),
    answerCount: z.number().default(0),
  }),
});

export const resumeOptimizationCompletedEvent = eventSchema.extend({
  event: z.string().default("resume_optimization_completed"),
  properties: z.object({
    resumeId: z.string(),
    success: z.boolean(),
    processingTime: z.number().optional(),
    errorMessage: z.string().optional(),
  }),
});

export const resumeOptimizationAcceptedEvent = eventSchema.extend({
  event: z.string().default("resume_optimization_accepted"),
  properties: z.object({
    resumeId: z.string(),
    optimizationId: z.string(),
  }),
});

export const resumeOptimizationRejectedEvent = eventSchema.extend({
  event: z.string().default("resume_optimization_rejected"),
  properties: z.object({
    resumeId: z.string(),
    optimizationId: z.string(),
  }),
});

// Optimization Questions Events
export const optimizationQuestionsGeneratedEvent = eventSchema.extend({
  event: z.string().default("optimization_questions_generated"),
  properties: z.object({
    resumeId: z.string(),
    questionCount: z.number(),
    success: z.boolean(),
    errorMessage: z.string().optional(),
  }),
});

export const optimizationQuestionsAnsweredEvent = eventSchema.extend({
  event: z.string().default("optimization_questions_answered"),
  properties: z.object({
    resumeId: z.string(),
    answerCount: z.number(),
    completionRate: z.number(), // percentage of questions answered
  }),
});

// Optimization Status Events
export const optimizationStatusCheckedEvent = eventSchema.extend({
  event: z.string().default("optimization_status_checked"),
  properties: z.object({
    resumeId: z.string(),
    isInProgress: z.boolean(),
    hasCompletedResults: z.boolean(),
  }),
});

export const optimizationResultsViewedEvent = eventSchema.extend({
  event: z.string().default("optimization_results_viewed"),
  properties: z.object({
    resumeId: z.string(),
    optimizationId: z.string(),
  }),
});

export const optimizationResultsDeletedEvent = eventSchema.extend({
  event: z.string().default("optimization_results_deleted"),
  properties: z.object({
    resumeId: z.string(),
    optimizationId: z.string(),
  }),
});

// Optimization Queue Events
export const optimizationQueueSubscribedEvent = eventSchema.extend({
  event: z.string().default("optimization_queue_subscribed"),
  properties: z.object({
    resumeId: z.string(),
    queueName: z.string(),
  }),
});

export const optimizationProgressUpdatedEvent = eventSchema.extend({
  event: z.string().default("optimization_progress_updated"),
  properties: z.object({
    resumeId: z.string(),
    step: z.string(),
    progress: z.number(), // percentage
    message: z.string().optional(),
  }),
});

// Type exports
export type ResumeOptimizationStartedEvent = z.infer<typeof resumeOptimizationStartedEvent>;
export type ResumeOptimizationCompletedEvent = z.infer<typeof resumeOptimizationCompletedEvent>;
export type ResumeOptimizationAcceptedEvent = z.infer<typeof resumeOptimizationAcceptedEvent>;
export type ResumeOptimizationRejectedEvent = z.infer<typeof resumeOptimizationRejectedEvent>;
export type OptimizationQuestionsGeneratedEvent = z.infer<
  typeof optimizationQuestionsGeneratedEvent
>;
export type OptimizationQuestionsAnsweredEvent = z.infer<typeof optimizationQuestionsAnsweredEvent>;
export type OptimizationStatusCheckedEvent = z.infer<typeof optimizationStatusCheckedEvent>;
export type OptimizationResultsViewedEvent = z.infer<typeof optimizationResultsViewedEvent>;
export type OptimizationResultsDeletedEvent = z.infer<typeof optimizationResultsDeletedEvent>;
export type OptimizationQueueSubscribedEvent = z.infer<typeof optimizationQueueSubscribedEvent>;
export type OptimizationProgressUpdatedEvent = z.infer<typeof optimizationProgressUpdatedEvent>;
